// src/componentes/GestionInterna/GestionInterna.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, query, Timestamp } from "firebase/firestore";
import { db } from "../../Firebase/firebaseconfig";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import AccessDenied from "../Auth/AccessDenied";
import {
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaChevronRight,
  FaCreditCard,
  FaExclamationTriangle,
  FaPen,
  FaPlusCircle,
  FaSearch,
} from "react-icons/fa";
import { money } from "../../services/cxpDeudaService";
import "./GestionInterna.css";

const normalize = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();

const hasContext = (item = {}) =>
  Boolean(item.proyectoId || item.fraccionamientoId || item.obraId || item.propiedadId);

const isCapitalizableSinContexto = (item = {}) =>
  item.capitalizable === true && !hasContext(item);

const contextLabel = (item = {}) =>
  [item.proyectoNombre, item.fraccionamientoNombre, item.obraNombre, item.propiedadFolio]
    .filter(Boolean)
    .join(" / ");

const GestionInterna = () => {
  const navigate = useNavigate();
  const { can } = useUserContext();

  const [filtro, setFiltro] = useState("corregir");
  const [items, setItems] = useState([]);
  const [busqueda, setBusqueda] = useState("");

  const [todayRange] = useState(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return {
      inicioDate: start,
      inicio: Timestamp.fromDate(start),
      fin: Timestamp.fromDate(end),
    };
  });

  useEffect(() => {
    if (!can("cxp.ver")) {
      setItems([]);
      return;
    }

    const q = query(collection(db, "cxp_deudas"));
    const unsub = onSnapshot(q, (snap) => {
      setItems(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsub();
  }, [can]);

  const resumen = useMemo(() => {
    const active = items.filter((item) => item.activo !== false);
    const canceladas = items.filter((item) => item.estado === "cancelada" || item.cancelada === true);
    const paid = items.filter(
      (item) =>
        item.activo === false &&
        item.estado !== "cancelada" &&
        item.cancelada !== true
    );
    const vencenHoy = active.filter((item) => {
      const due = item.nextDueDate;
      return due && due >= todayRange.inicio && due <= todayRange.fin;
    });
    const enMora = active.filter((item) => item.nextDueDate && item.nextDueDate < todayRange.inicio);
    const sinContexto = active.filter((item) => !hasContext(item));
    const capitalizableSinContexto = active.filter(isCapitalizableSinContexto);

    return {
      active,
      paid,
      canceladas,
      vencenHoy,
      enMora,
      sinContexto,
      capitalizableSinContexto,
    };
  }, [items, todayRange]);

  const listaBase = useMemo(() => {
    if (filtro === "hoy") return resumen.vencenHoy;
    if (filtro === "mora") return resumen.enMora;
    if (filtro === "activas") return resumen.active;
    if (filtro === "pagadas") return resumen.paid;
    if (filtro === "canceladas") return resumen.canceladas;
    return resumen.sinContexto;
  }, [filtro, resumen]);

  const listaFiltrada = useMemo(() => {
    const q = normalize(busqueda);
    const filtered = q
      ? listaBase.filter((item) =>
          normalize(
            [
              item.proveedorNombre,
              item.referencia,
              item.tipoCosto,
              item.proyectoNombre,
              item.fraccionamientoNombre,
              item.obraNombre,
              item.propiedadFolio,
            ]
              .filter(Boolean)
              .join(" ")
          ).includes(q)
        )
      : listaBase;

    return [...filtered].sort((a, b) => Number(b.saldoRestante || 0) - Number(a.saldoRestante || 0));
  }, [listaBase, busqueda]);

  if (!can("gestionInterna.ver")) {
    return <AccessDenied message="No tienes permiso para ver gestion interna." />;
  }

  return (
    <div className="gi-page">
      <div className="gi-panel">
        <header className="gi-header">
          <div>
            <p className="gi-eyebrow">Operacion interna</p>
            <h1>Gestion interna</h1>
            <p>Control de CxP, proveedores y consistencia ERP.</p>
          </div>
          {can("cxp.crear") && (
            <button className="gi-primary-btn" type="button" onClick={() => navigate("/app/cxp/nueva")}>
              <FaPlusCircle /> Nueva CxP
            </button>
          )}
        </header>

        <section className="gi-kpis">
          <Kpi icon={FaExclamationTriangle} label="Por corregir" value={resumen.sinContexto.length} tone="warning" />
          <Kpi icon={FaCalendarAlt} label="Vencen hoy" value={resumen.vencenHoy.length} />
          <Kpi icon={FaCreditCard} label="En mora" value={resumen.enMora.length} tone="risk" />
          <Kpi icon={FaBuilding} label="Activas" value={resumen.active.length} />
          <Kpi icon={FaCheckCircle} label="Pagadas" value={resumen.paid.length} tone="good" />
          <Kpi icon={FaExclamationTriangle} label="Canceladas" value={resumen.canceladas.length} tone="neutral" />
        </section>

        {resumen.capitalizableSinContexto.length > 0 && (
          <div className="gi-alert">
            <FaExclamationTriangle />
            <div>
              <strong>{resumen.capitalizableSinContexto.length} CxP capitalizables sin contexto.</strong>
              <span>
                Estas cuentas no se sumaran correctamente a proyecto, fraccionamiento, obra o lote hasta editarlas.
              </span>
            </div>
          </div>
        )}

        <section className="gi-grid">
          {can("cxp.ver") && (
            <ModuleCard
              title="Agenda CxP"
              description="Vencimientos, pagos rapidos y cuentas atrasadas."
              action="Abrir agenda"
              onClick={() => navigate("/app/cxp")}
            />
          )}

          {can("cxp.crear") && (
            <ModuleCard
              title="Nueva CxP"
              description="Registra una cuenta por pagar con clasificacion ERP."
              action="Crear"
              icon={FaPlusCircle}
              onClick={() => navigate("/app/cxp/nueva")}
            />
          )}

          {can("proveedores.ver") && (
            <ModuleCard
              title="Proveedores"
              description="Catalogo, contactos y detalle de proveedores."
              action="Abrir modulo"
              onClick={() => navigate("/app/proveedores")}
            />
          )}
        </section>

        <section className="gi-list-section">
          <div className="gi-list-head">
            <div>
              <h2>Correccion y seguimiento de CxP</h2>
              <p>Usa "Por corregir" para encontrar CxP administrativas o sin contexto financiero.</p>
            </div>
            <div className="gi-search">
              <FaSearch />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar proveedor, referencia, proyecto, obra..."
              />
            </div>
          </div>

          <div className="gi-pills">
            <FilterButton active={filtro === "corregir"} onClick={() => setFiltro("corregir")}>
              Por corregir ({resumen.sinContexto.length})
            </FilterButton>
            <FilterButton active={filtro === "hoy"} onClick={() => setFiltro("hoy")}>
              Vencen hoy ({resumen.vencenHoy.length})
            </FilterButton>
            <FilterButton active={filtro === "mora"} onClick={() => setFiltro("mora")}>
              En mora ({resumen.enMora.length})
            </FilterButton>
            <FilterButton active={filtro === "activas"} onClick={() => setFiltro("activas")}>
              Activas ({resumen.active.length})
            </FilterButton>
            <FilterButton active={filtro === "pagadas"} onClick={() => setFiltro("pagadas")}>
              Pagadas ({resumen.paid.length})
            </FilterButton>
            <FilterButton active={filtro === "canceladas"} onClick={() => setFiltro("canceladas")}>
              Canceladas ({resumen.canceladas.length})
            </FilterButton>
          </div>

          <div className="gi-table-wrap">
            <table className="gi-table">
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>Referencia</th>
                  <th>Tipo</th>
                  <th>Contexto ERP</th>
                  <th>Saldo</th>
                  <th>Proximo pago</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listaFiltrada.map((item) => {
                  const contexto = contextLabel(item);
                  const needsFix = !hasContext(item);
                  return (
                    <tr key={item.id}>
                      <td>
                        <strong>{item.proveedorNombre || "Proveedor"}</strong>
                        {item.capitalizable ? <span className="gi-mini-badge">capitalizable</span> : null}
                        {item.estado === "cancelada" || item.cancelada ? (
                          <span className="gi-mini-badge gi-mini-badge--danger">cancelada</span>
                        ) : null}
                      </td>
                      <td>{item.referencia || "-"}</td>
                      <td>{item.tipoCosto || "administrativo"}</td>
                      <td>
                        {contexto ? (
                          <span className="gi-context-ok">{contexto}</span>
                        ) : (
                          <span className="gi-context-missing">Sin contexto</span>
                        )}
                      </td>
                      <td>{money(item.saldoRestante)}</td>
                      <td>{item.nextDueDate?.toDate?.().toLocaleDateString?.() || "-"}</td>
                      <td>
                        <div className="gi-actions">
                          <button type="button" onClick={() => navigate(`/app/cxp/deudas/${item.id}`)}>
                            Ver
                          </button>
                          {can("cxp.editar") && (
                            <button
                              type="button"
                              className={needsFix ? "gi-fix-btn" : ""}
                              onClick={() => navigate(`/app/cxp/deudas/${item.id}/editar`)}
                            >
                              <FaPen /> {needsFix ? "Corregir" : "Editar"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {listaFiltrada.length === 0 && (
                  <tr>
                    <td colSpan="7" className="gi-empty">
                      No hay CxP para este filtro.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <footer className="gi-footnote">
          Para que una CxP afecte utilidad, debe tener contexto ERP y marcarse como capitalizable cuando corresponda.
        </footer>
      </div>
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, tone = "neutral" }) => (
  <div className={`gi-card gi-kpi gi-kpi--${tone}`}>
    <div className="gi-icon">
      <Icon />
    </div>
    <div>
      <div className="gi-kpi-label">{label}</div>
      <div className="gi-kpi-value">{value}</div>
    </div>
  </div>
);

const ModuleCard = ({ title, description, action, onClick, icon: Icon = FaChevronRight }) => (
  <div className="gi-card">
    <h3>{title}</h3>
    <p>{description}</p>
    <button className="gi-link" type="button" onClick={onClick}>
      {action} <Icon />
    </button>
  </div>
);

const FilterButton = ({ active, onClick, children }) => (
  <button className={`gi-pill ${active ? "active" : ""}`} type="button" onClick={onClick}>
    {children}
  </button>
);

export default GestionInterna;
